import React, { createContext, useContext, useEffect, useState } from 'react';

/**
 * Oferta individual de 2 horas por visitante.
 * - En la primera visita se guarda el vencimiento (ahora + 2 h) en localStorage.
 * - Al recargar o volver a entrar desde el mismo navegador se usa el vencimiento guardado:
 *   no se reinicia y el tiempo sigue corriendo aunque la pestaña esté cerrada.
 * - Al llegar a cero, `expired` pasa a true y la landing retira el precio promocional.
 *
 * IMPORTANTE: esto solo afecta a la landing. El checkout de ImpulTienda es independiente
 * y debe configurarse por separado para respetar el mismo vencimiento.
 */
const STORAGE_KEY = 'bsdr-offer-expires-at';
export const OFFER_DURATION_MS = 2 * 60 * 60 * 1000;
type OfferTimer = {
  /** Milisegundos restantes (0 cuando venció) */
  remaining: number;
  expired: boolean;
  /** false hasta leer localStorage, para no mostrar un estado incorrecto al cargar */
  ready: boolean;
};
const OfferTimerContext = createContext<OfferTimer>({
  remaining: OFFER_DURATION_MS,
  expired: false,
  ready: false
});
export function OfferTimerProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setExpiresAt(readOrCreateExpiry());
  }, []);
  useEffect(() => {
    if (expiresAt === null) return;
    if (Date.now() >= expiresAt) {
      setNow(Date.now());
      return;
    }
    const id = window.setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= expiresAt) window.clearInterval(id);
    }, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);
  const remaining = expiresAt === null ? OFFER_DURATION_MS : Math.max(0, expiresAt - now);
  const value: OfferTimer = {
    remaining,
    expired: expiresAt !== null && remaining <= 0,
    ready: expiresAt !== null
  };
  return <OfferTimerContext.Provider value={value} data-id="element-380" data-mp="2sfthwye9a3n7">{children}</OfferTimerContext.Provider>;
}
export function useOfferTimer() {
  return useContext(OfferTimerContext);
}
function readOrCreateExpiry(): number {
  const fallback = Date.now() + OFFER_DURATION_MS;
  try {
    const stored = Number(window.localStorage.getItem(STORAGE_KEY));
    if (Number.isFinite(stored) && stored > 0) return stored;
    window.localStorage.setItem(STORAGE_KEY, String(fallback));
  } catch {
    // localStorage no disponible (modo privado estricto): la oferta dura 2 h en esta visita
  }
  return fallback;
}